import React, { useState } from "react";
import { Autocomplete, TextField, Typography } from "@mui/material";
import { titleize } from "inflection";
import { filter } from "lodash";
import { useMutation } from "@apollo/client/react";
import OverwatchModal from "@/components/OverwatchModal";
import { User, UpdateEmployeeDocument, EmploymentStatusEnum } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";

interface RemoveResourceModalProps {
  employees: User[],
  resourceId: string;
  handleClose: () => void;
}
const RemoveResourceModal: React.FC<RemoveResourceModalProps> = ({
  employees,
  resourceId,
  handleClose,
}) => {
  const [showEmployeesList, setShowEmployeesList] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    onCompleted: () => {
      displayNotification({ type: "success", text: "Resource updated" });
      handleClose();
    },
    onError: () => displayNotification({ type: "error", text: "Resource could not be updated" }),
    refetchQueries: ["getAllEmployees"]
  });

  const handlePrimaryAction = () => {
    if (!showEmployeesList) {
      setShowEmployeesList(true);
    } else {
      updateEmployee({
        variables: {
          user: { id: resourceId, companyEmploymentStatus: EmploymentStatusEnum.Inactive, updatedScheduleId: selectedEmployee?.id }
        }
      });
    }
  }

  const handleSecondaryClick = () => {
    updateEmployee({
      variables: {
        user: { id: resourceId, companyEmploymentStatus: EmploymentStatusEnum.Inactive, ignoreSchedules: true }
      }
    })
  }

  return (
    <OverwatchModal
      size="sm"
      title="Remove Resource"
      caption="Are you sure you want to remove this resource?"
      handleClose={handleClose}
      primaryButtonAction={handlePrimaryAction}
      primaryButtonText={showEmployeesList ? "Reassign & Remove" : "Handle Now"}
      secondaryButtonText="Ignore"
      disableSecondary={showEmployeesList}
      secondaryButtonAction={handleSecondaryClick}
    >
      {showEmployeesList ?
        <Autocomplete
          options={filter((employees), (employee) => (employee.companyEmployment?.status === EmploymentStatusEnum.Active && resourceId !== employee.id))}
          getOptionLabel={(option) => titleize(`${option.firstName} ${option.lastName}`)}
          onChange={(_, value) => setSelectedEmployee(value)}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Select Employee" variant="outlined" />}
        />
        : (
          <Typography color="error">There are active schedules that need to be taken care of.</Typography>
        )}
    </OverwatchModal>
  )
};

export default RemoveResourceModal;
